

import { Calendar, Users, Package, MapPin, Clock, FileText, CheckSquare, Receipt } from "lucide-react";
import { formatDate } from "@/lib/data";
import { EventDetail } from "./types";

// Helper function to format time from datetime string
function formatEventTime(dateString: string): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-IN", { 
    hour: "2-digit", 
    minute: "2-digit",
    hour12: true 
  });
}

interface EventDetailsProps {
  event: EventDetail;
  tasksCount?: number;
  invoicesCount?: number;
}

export default function EventDetails({ event, tasksCount = 0, invoicesCount = 0 }: EventDetailsProps) {
  // Extract time from start_date if it's a datetime string
  const eventDate = event.start_date?.split('T')[0] || event.start_date;
  const eventTime = event.start_date?.includes('T') ? event.start_date.split('T')[1]?.substring(0, 5) : null;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-100">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-600" />
          Event Details
        </h3>
      </div>
      <div className="p-4 space-y-4">
        {/* Date and Time Row */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 border border-amber-100">
            <Calendar className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wide">Event Date</p>
            <p className="text-sm font-semibold text-stone-800">{formatDate(event.start_date)}</p>
            {event.end_date && event.end_date !== event.start_date && (
              <p className="text-xs text-stone-400">to {formatDate(event.end_date)}</p>
            )}
          </div>
        </div>

        {/* Time (if available) */}
        {eventTime && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 border border-blue-100">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Event Time</p>
              <p className="text-sm font-semibold text-stone-800">{formatEventTime(event.start_date)}</p>
            </div>
          </div>
        )}

        {/* Guests Row */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0 border border-indigo-100">
            <Users className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wide">Guest Count</p>
            <p className="text-sm font-semibold text-stone-800">{event.guest_count || 0} people</p>
          </div>
        </div>

        {/* Tasks & Invoices Row */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="flex items-center gap-2 p-2 bg-violet-50 rounded-lg border border-violet-100">
            <CheckSquare className="w-4 h-4 text-violet-600" />
            <div>
              <p className="text-[10px] text-violet-600 uppercase tracking-wide">Tasks</p>
              <p className="text-sm font-semibold text-violet-800">{tasksCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
            <Receipt className="w-4 h-4 text-amber-600" />
            <div>
              <p className="text-[10px] text-amber-600 uppercase tracking-wide">Invoices</p>
              <p className="text-sm font-semibold text-amber-800">{invoicesCount}</p>
            </div>
          </div>
        </div>

        {/* Package Row */}
        {event.package && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 border border-emerald-100">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Package</p>
              <p className="text-sm font-semibold text-stone-800">{event.package.name}</p>
            </div>
          </div>
        )}

        {/* Venue Row */}
        {event.venue && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0 border border-rose-100">
              <MapPin className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Venue</p>
              <p className="text-sm font-semibold text-stone-800">{event.venue.name}</p>
            </div>
          </div>
        )}

        {/* Notes Row - if there's important notes */}
        {event.notes && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-stone-50 flex items-center justify-center flex-shrink-0 border border-stone-200">
                <FileText className="w-5 h-5 text-stone-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Notes</p>
                <p className="text-sm text-stone-700 mt-1">{event.notes}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
